import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { getAllAgents, updateAgent, deleteAgent, getAgentById, getAllBureaux, getAllCellules, getAllGrades, getAllFonctions, getAllCategoriesProfessionnelles } from '../services/api';

const Recherche = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [allAgents, setAllAgents] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editPhoto, setEditPhoto] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [userRole, setUserRole] = useState(null);
  
  // Filtres avancés
  const [filters, setFilters] = useState({
    statut: '',
    bureau: '',
    cellule: '',
    grade: '',
    fonction: '',
    categorie: '',
    sexe: '',
    zone_affectation: '',
    lieu_affectation: '',
    date_naissance_from: '',
    date_naissance_to: '',
    date_affectation_from: '',
    date_affectation_to: '',
    date_enregistrement_from: '',
    date_enregistrement_to: '',
  });
  
  // Options de tri
  const [sortBy, setSortBy] = useState('date_enregistrement');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Options pour les select
  const [bureaux, setBureaux] = useState([]);
  const [cellules, setCellules] = useState([]);
  const [grades, setGrades] = useState([]);
  const [fonctions, setFonctions] = useState([]);
  const [categories, setCategories] = useState([]);

  // Options pour les selecteurs
  const situationPrimeOptions = [
    { value: 'P', label: 'Primé (P)' },
    { value: 'NP', label: 'Non Primé (N.P)' },
  ];

  const etatCivilOptions = [
    { value: 'celibataire', label: 'Célibataire' },
    { value: 'marie', label: 'Marié(e)' },
    { value: 'divorce', label: 'Divorcé(e)' },
    { value: 'veuf', label: 'Veuf(ve)' },
    { value: 'separe', label: 'Séparé(e)' },
  ];

  const titreAcademiqueOptions = [
    { value: 'aucun', label: 'Aucun' },
    { value: 'cepe', label: 'CEPE (Certificat d\'Études Primaires Élémentaires)' },
    { value: 'brevet', label: 'Brevet d\'Études du Premier Cycle (BEPC)' },
    { value: 'baccalaureat', label: 'Diplôme d\'état' },
    { value: 'diplome_technique', label: 'Diplôme Technique' },
    { value: 'licence', label: 'Licence' },
    { value: 'master', label: 'Master' },
    { value: 'doctorat', label: 'Doctorat' },
    { value: 'autre', label: 'Autre' },
  ];

  useEffect(() => {
    loadAllAgents();
    loadFilterOptions();
    // Récupérer le rôle de l'utilisateur depuis localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setUserRole(user.role || null);
      } catch (error) {
        console.error('Erreur lors du parsing de l\'utilisateur:', error);
      }
    }
  }, []);

  // Appliquer les filtres automatiquement quand les agents sont chargés ou quand les filtres changent
  useEffect(() => {
    if (allAgents.length > 0) {
      applyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAgents.length]);

  // Appliquer les filtres avec un léger délai pour éviter trop de re-renders
  useEffect(() => {
    if (allAgents.length > 0) {
      const timeoutId = setTimeout(() => {
        applyFilters();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortBy, sortOrder, searchTerm]);

  const loadFilterOptions = async () => {
    try {
      const [bureauxData, cellulesData, gradesData, fonctionsData, categoriesData] = await Promise.all([
        getAllBureaux(),
        getAllCellules(),
        getAllGrades(),
        getAllFonctions(),
        getAllCategoriesProfessionnelles(),
      ]);
      setBureaux(Array.isArray(bureauxData) ? bureauxData : []);
      setCellules(Array.isArray(cellulesData) ? cellulesData : []);
      setGrades(Array.isArray(gradesData) ? gradesData : []);
      setFonctions(Array.isArray(fonctionsData) ? fonctionsData : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
      console.error('Erreur lors du chargement des options de filtres:', error);
    }
  };

  const loadAllAgents = async () => {
    setLoading(true);
    try {
      const data = await getAllAgents();
      // S'assurer que data est un tableau
      const agentsArray = Array.isArray(data) ? data : [];
      setAllAgents(agentsArray);
      setAgents(agentsArray);
    } catch (error) {
      console.error('Erreur lors du chargement des agents:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des agents' });
      setAllAgents([]);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allAgents];

    // Recherche textuelle
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((agent) => {
        return (
          agent.nom?.toLowerCase().includes(term) ||
          agent.prenom?.toLowerCase().includes(term) ||
          agent.matricule?.toLowerCase().includes(term) ||
          agent.bureau_nom?.toLowerCase().includes(term) ||
          agent.bureau?.toLowerCase().includes(term) ||
          agent.cellule_nom?.toLowerCase().includes(term) ||
          agent.cellule?.toLowerCase().includes(term) ||
          agent.telephone?.toLowerCase().includes(term) ||
          agent.email?.toLowerCase().includes(term) ||
          agent.grade?.toLowerCase().includes(term) ||
          agent.fonction?.toLowerCase().includes(term) ||
          agent.categorie?.toLowerCase().includes(term) ||
          agent.zone_affectation?.toLowerCase().includes(term) ||
          agent.lieu_affectation?.toLowerCase().includes(term) ||
          agent.ref_affectation?.toLowerCase().includes(term)
        );
      });
    }

    // Filtres avancés
    if (filters.statut) {
      filtered = filtered.filter(agent => agent.statut === filters.statut);
    }
    if (filters.bureau) {
      filtered = filtered.filter(agent => {
        const bureauValue = agent.bureau || agent.bureau_nom;
        return String(bureauValue) === String(filters.bureau) || bureauValue === filters.bureau;
      });
    }
    if (filters.cellule) {
      filtered = filtered.filter(agent => {
        const celluleValue = agent.cellule || agent.cellule_nom;
        return String(celluleValue) === String(filters.cellule) || celluleValue === filters.cellule;
      });
    }
    if (filters.grade) {
      filtered = filtered.filter(agent => {
        const gradeValue = agent.grade;
        return String(gradeValue) === String(filters.grade) || gradeValue === filters.grade;
      });
    }
    if (filters.fonction) {
      filtered = filtered.filter(agent => {
        const fonctionValue = agent.fonction;
        return String(fonctionValue) === String(filters.fonction) || fonctionValue === filters.fonction;
      });
    }
    if (filters.categorie) {
      filtered = filtered.filter(agent => {
        const categorieValue = agent.categorie;
        return String(categorieValue) === String(filters.categorie) || categorieValue === filters.categorie;
      });
    }
    if (filters.sexe) {
      filtered = filtered.filter(agent => agent.sexe === filters.sexe);
    }
    if (filters.zone_affectation) {
      filtered = filtered.filter(agent => 
        agent.zone_affectation?.toLowerCase().includes(filters.zone_affectation.toLowerCase())
      );
    }
    if (filters.lieu_affectation) {
      filtered = filtered.filter(agent => 
        agent.lieu_affectation?.toLowerCase().includes(filters.lieu_affectation.toLowerCase())
      );
    }

    // Filtres par dates
    if (filters.date_naissance_from) {
      filtered = filtered.filter(agent => {
        if (!agent.date_naissance) return false;
        return new Date(agent.date_naissance) >= new Date(filters.date_naissance_from);
      });
    }
    if (filters.date_naissance_to) {
      filtered = filtered.filter(agent => {
        if (!agent.date_naissance) return false;
        return new Date(agent.date_naissance) <= new Date(filters.date_naissance_to);
      });
    }
    if (filters.date_affectation_from) {
      filtered = filtered.filter(agent => {
        if (!agent.date_affectation) return false;
        return new Date(agent.date_affectation) >= new Date(filters.date_affectation_from);
      });
    }
    if (filters.date_affectation_to) {
      filtered = filtered.filter(agent => {
        if (!agent.date_affectation) return false;
        return new Date(agent.date_affectation) <= new Date(filters.date_affectation_to);
      });
    }
    if (filters.date_enregistrement_from) {
      filtered = filtered.filter(agent => {
        if (!agent.date_enregistrement) return false;
        return new Date(agent.date_enregistrement) >= new Date(filters.date_enregistrement_from);
      });
    }
    if (filters.date_enregistrement_to) {
      filtered = filtered.filter(agent => {
        if (!agent.date_enregistrement) return false;
        return new Date(agent.date_enregistrement) <= new Date(filters.date_enregistrement_to);
      });
    }

    // Tri
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Gérer les valeurs nulles
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      // Convertir en dates si nécessaire
      if (sortBy.includes('date')) {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    setAgents(filtered);
    setCurrentPage(1);
    setMessage({ type: 'success', text: `${filtered.length} agent(s) trouvé(s)` });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilters({
      statut: '',
      bureau: '',
      cellule: '',
      grade: '',
      fonction: '',
      categorie: '',
      sexe: '',
      zone_affectation: '',
      lieu_affectation: '',
      date_naissance_from: '',
      date_naissance_to: '',
      date_affectation_from: '',
      date_affectation_to: '',
      date_enregistrement_from: '',
      date_enregistrement_to: '',
    });
    setSortBy('date_enregistrement');
    setSortOrder('desc');
  };

  const handleStatutChange = async (agentId, newStatut) => {
    try {
      await updateAgent(agentId, { statut: newStatut });
      setMessage({ type: 'success', text: 'Statut mis à jour avec succès' });
      loadAllAgents();
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour du statut' });
    }
  };

  const handleDeleteAgent = async (agentId, agentName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'agent "${agentName}" ?\nCette action est irréversible.`)) {
      return;
    }

    try {
      await deleteAgent(agentId);
      setMessage({ type: 'success', text: 'Agent supprimé avec succès' });
      loadAllAgents();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la suppression de l\'agent',
      });
    }
  };

  const viewCarte = (matricule) => {
    const API_URL = process.env.REACT_APP_API_URL || 'https://idsysbackend.onrender.com/api';
    const token = localStorage.getItem('token');
    if (token && matricule) {
      window.open(`${API_URL}/agents/carte/${matricule}?token=${token}`, '_blank');
    }
  };

  // Calculs de pagination
  const totalPages = Math.ceil((Array.isArray(agents) ? agents.length : 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAgents = Array.isArray(agents) ? agents.slice(startIndex, endIndex) : [];

  // Gestion de la pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll vers le haut de la table
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Réinitialiser à la page 1 quand les agents changent
  useEffect(() => {
    setCurrentPage(1);
  }, [agents.length]);

  // Fonction pour afficher les détails d'un agent
  const handleViewDetails = async (agentId) => {
    setLoadingDetails(true);
    setShowDetails(true);
    try {
      const response = await getAgentById(agentId);
      // Gérer différentes structures de réponse
      const agentDetails = response.data || response.agent || response;
      console.log('Détails agent chargés:', agentDetails);
      console.log('Photo présente:', !!agentDetails.photo);
      console.log('Documents:', agentDetails.documents);
      console.log('Type photo:', typeof agentDetails.photo);
      if (agentDetails.photo && typeof agentDetails.photo !== 'string') {
        console.warn('Photo n\'est pas une chaîne, conversion nécessaire');
      }
      setSelectedAgent(agentDetails);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors du chargement des détails de l\'agent',
      });
      setShowDetails(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedAgent(null);
  };

  // Fonction pour ouvrir le formulaire de modification
  const handleEditAgent = async (agentId) => {
    setEditLoading(true);
    setShowEditForm(true);
    try {
      const response = await getAgentById(agentId);
      const agentData = response.data || response.agent || response;
      setEditingAgent(agentData);
      
      // Pré-remplir le formulaire avec les données de l'agent
      setEditFormData({
        nom: agentData.nom || '',
        prenom: agentData.prenom || '',
        post_nom: agentData.post_nom || '',
        sexe: agentData.sexe || '',
        etat_civil: agentData.etat_civil || '',
        titre_academique: agentData.titre_academique || '',
        filiere: agentData.filiere || '',
        date_naissance: agentData.date_naissance ? agentData.date_naissance.split('T')[0] : '',
        lieu_naissance: agentData.lieu_naissance || '',
        nationalite: agentData.nationalite || 'Congolaise',
        telephone: agentData.telephone || '',
        email: agentData.email || '',
        adresse: agentData.adresse || '',
        id_agent: agentData.id_agent || '',
        matricule: agentData.matricule || '',
        categorie: agentData.categorie || '',
        bureau: agentData.bureau || '',
        cellule: agentData.cellule || '',
        grade: agentData.grade || '',
        fonction: agentData.fonction || '',
        date_affectation: agentData.date_affectation ? agentData.date_affectation.split('T')[0] : '',
        ref_affectation: agentData.ref_affectation || '',
        zone_affectation: agentData.zone_affectation || '',
        lieu_affectation: agentData.lieu_affectation || '',
        situation_prime: agentData.situation_prime || '',
        arrete: agentData.arrete === 1 || agentData.arrete === true,
        commission_affectation_sg: agentData.commission_affectation_sg === 1 || agentData.commission_affectation_sg === true,
        commission_affectation_locale: agentData.commission_affectation_locale === 1 || agentData.commission_affectation_locale === true,
        notification_fonc_publique_ville: agentData.notification_fonc_publique_ville === 1 || agentData.notification_fonc_publique_ville === true,
        commission_local_fonc_publique_ville: agentData.commission_local_fonc_publique_ville === 1 || agentData.commission_local_fonc_publique_ville === true,
        notification_nu: agentData.notification_nu === 1 || agentData.notification_nu === true,
      });
      
      // Pré-remplir la photo si disponible
      if (agentData.photo && typeof agentData.photo === 'string' && agentData.photo.length > 0) {
        setEditPhotoPreview(agentData.photo);
      } else {
        setEditPhotoPreview(null);
      }
      setEditPhoto(null);
    } catch (error) {
      console.error('Erreur lors du chargement des données de l\'agent:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des données de l\'agent' });
      setShowEditForm(false);
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditForm = () => {
    setShowEditForm(false);
    setEditingAgent(null);
    setEditFormData({});
    setEditPhoto(null);
    setEditPhotoPreview(null);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditPhoto(file);
      setEditPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const data = new FormData();
      
      // Ajouter tous les champs du formulaire
      Object.keys(editFormData).forEach((key) => {
        const value = editFormData[key];
        if (value !== null && value !== undefined && (value !== '' || typeof value === 'boolean')) {
          if (typeof value === 'boolean') {
            data.append(key, value ? 'true' : 'false');
          } else if (typeof value === 'string' || typeof value === 'number') {
            data.append(key, String(value));
          }
        }
      });

      if (editPhoto) {
        data.append('photo', editPhoto);
      }

      await updateAgent(editingAgent.id, data);
      setMessage({ type: 'success', text: 'Agent modifié avec succès!' });
      closeEditForm();
      loadAllAgents();
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la modification de l\'agent',
      });
    } finally {
      setEditLoading(false);
    }
  };

  // Vérifier si la catégorie sélectionnée est "sous statut" ou "nouvelle unité"
  const getSelectedCategorieLabel = (categorieValue) => {
    if (!categorieValue) return '';
    const selected = categories.find(
      (c) => (c.id || c.nom) == categorieValue || c.nom === categorieValue
    );
    return selected ? selected.nom.toLowerCase() : '';
  };

  const isSousStatut = (categorieValue) => {
    const label = getSelectedCategorieLabel(categorieValue);
    return label.includes('sous statut') || label === 'sous statut';
  };

  const isNouvelleUnite = (categorieValue) => {
    const label = getSelectedCategorieLabel(categorieValue);
    return label.includes('nouvelle unité') || label.includes('nouvelle unite') || label === 'nouvelle unité' || label === 'nouvelle unite';
  };

  // Fonctions helper pour obtenir les noms à partir des IDs
  const getGradeName = (gradeId) => {
    if (!gradeId) return null;
    const grade = grades.find((g) => {
      const gradeValue = g.id || g.nom;
      return String(gradeValue) === String(gradeId) || g.nom === gradeId;
    });
    return grade ? (grade.nom || String(grade.id || gradeId)) : gradeId;
  };

  const getFonctionName = (fonctionId) => {
    if (!fonctionId) return null;
    const fonction = fonctions.find((f) => {
      const fonctionValue = f.id || f.nom;
      return String(fonctionValue) === String(fonctionId) || f.nom === fonctionId;
    });
    return fonction ? (fonction.nom || String(fonction.id || fonctionId)) : fonctionId;
  };

  return (
    <div className="p-8 bg-background min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-text mb-8 flex items-center">
          <i className="fas fa-search mr-3 text-primary"></i>
          Rechercher un Agent
        </h1>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="bg-cardBg rounded-lg shadow-lg p-6 mb-6">
          <div className="space-y-4">
            {/* Recherche principale */}
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher par nom, prénom, matricule, bureau, cellule, grade, fonction, catégorie, zone, lieu..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all disabled:opacity-50"
              >
                {loading ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <>
                    <i className="fas fa-search mr-2"></i>Rechercher
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={loadAllAgents}
                className="px-6 py-3 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-all"
              >
                <i className="fas fa-sync mr-2"></i>Actualiser
              </button>
            </div>

            {/* Options de tri et filtres avancés */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-text">Trier par:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                >
                  <option value="date_enregistrement">Date d'enregistrement</option>
                  <option value="nom">Nom</option>
                  <option value="prenom">Prénom</option>
                  <option value="matricule">Matricule</option>
                  <option value="date_naissance">Date de naissance</option>
                  <option value="date_affectation">Date d'affectation</option>
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                >
                  <option value="desc">Décroissant</option>
                  <option value="asc">Croissant</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all text-sm"
                >
                  <i className={`fas fa-${showAdvancedFilters ? 'chevron-up' : 'chevron-down'} mr-2`}></i>
                  Filtres Avancés
                </button>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-all text-sm"
                >
                  <i className="fas fa-redo mr-2"></i>Réinitialiser
                </button>
              </div>
            </div>

            {/* Filtres avancés (pliables) */}
            {showAdvancedFilters && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Statut */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Statut</label>
                    <select
                      value={filters.statut}
                      onChange={(e) => handleFilterChange('statut', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    >
                      <option value="">Tous</option>
                      <option value="actif">Actif</option>
                      <option value="inactif">Inactif</option>
                    </select>
                  </div>

                  {/* Sexe */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Sexe</label>
                    <select
                      value={filters.sexe}
                      onChange={(e) => handleFilterChange('sexe', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    >
                      <option value="">Tous</option>
                      <option value="M">Masculin</option>
                      <option value="F">Féminin</option>
                    </select>
                  </div>

                  {/* Bureau */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Bureau</label>
                    <Select
                      value={filters.bureau && bureaux.length > 0
                        ? bureaux
                            .filter((b) => {
                              const bureauValue = b.id || b.nom;
                              return String(bureauValue) === String(filters.bureau) || b.nom === filters.bureau;
                            })
                            .map((b) => ({
                              value: b.id || b.nom,
                              label: b.nom || String(b.id || b.nom),
                            }))[0] || null
                        : null
                      }
                      onChange={(option) => handleFilterChange('bureau', option ? option.value : '')}
                      options={bureaux.map((b) => ({
                        value: b.id || b.nom,
                        label: b.nom || String(b.id || b.nom),
                      }))}
                      isClearable
                      placeholder="Tous les bureaux"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: '#d1d5db',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#00A896',
                          },
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected
                            ? '#00A896'
                            : state.isFocused
                            ? '#E0F7F5'
                            : 'white',
                          color: state.isSelected ? 'white' : '#333',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#00A896' : '#E0F7F5',
                          },
                        }),
                      }}
                    />
                  </div>

                  {/* Cellule */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Cellule</label>
                    <Select
                      value={filters.cellule && cellules.length > 0
                        ? cellules
                            .filter((c) => {
                              const celluleValue = c.id || c.nom;
                              return String(celluleValue) === String(filters.cellule) || c.nom === filters.cellule;
                            })
                            .map((c) => ({
                              value: c.id || c.nom,
                              label: c.nom || String(c.id || c.nom),
                            }))[0] || null
                        : null
                      }
                      onChange={(option) => handleFilterChange('cellule', option ? option.value : '')}
                      options={cellules.map((c) => ({
                        value: c.id || c.nom,
                        label: c.nom || String(c.id || c.nom),
                      }))}
                      isClearable
                      placeholder="Toutes les cellules"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: '#d1d5db',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#00A896',
                          },
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected
                            ? '#00A896'
                            : state.isFocused
                            ? '#E0F7F5'
                            : 'white',
                          color: state.isSelected ? 'white' : '#333',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#00A896' : '#E0F7F5',
                          },
                        }),
                      }}
                    />
                  </div>

                  {/* Grade */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Grade</label>
                    <Select
                      value={filters.grade && grades.length > 0
                        ? grades
                            .filter((g) => {
                              const gradeValue = g.id || g.nom;
                              return String(gradeValue) === String(filters.grade) || g.nom === filters.grade;
                            })
                            .map((g) => ({
                              value: g.id || g.nom,
                              label: g.nom || String(g.id || g.nom),
                            }))[0] || null
                        : null
                      }
                      onChange={(option) => handleFilterChange('grade', option ? option.value : '')}
                      options={grades.map((g) => ({
                        value: g.id || g.nom,
                        label: g.nom || String(g.id || g.nom),
                      }))}
                      isClearable
                      placeholder="Tous les grades"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: '#d1d5db',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#00A896',
                          },
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected
                            ? '#00A896'
                            : state.isFocused
                            ? '#E0F7F5'
                            : 'white',
                          color: state.isSelected ? 'white' : '#333',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#00A896' : '#E0F7F5',
                          },
                        }),
                      }}
                    />
                  </div>

                  {/* Fonction */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Fonction</label>
                    <Select
                      value={filters.fonction && fonctions.length > 0
                        ? fonctions
                            .filter((f) => {
                              const fonctionValue = f.id || f.nom;
                              return String(fonctionValue) === String(filters.fonction) || f.nom === filters.fonction;
                            })
                            .map((f) => ({
                              value: f.id || f.nom,
                              label: f.nom || String(f.id || f.nom),
                            }))[0] || null
                        : null
                      }
                      onChange={(option) => handleFilterChange('fonction', option ? option.value : '')}
                      options={fonctions.map((f) => ({
                        value: f.id || f.nom,
                        label: f.nom || String(f.id || f.nom),
                      }))}
                      isClearable
                      placeholder="Toutes les fonctions"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: '#d1d5db',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#00A896',
                          },
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected
                            ? '#00A896'
                            : state.isFocused
                            ? '#E0F7F5'
                            : 'white',
                          color: state.isSelected ? 'white' : '#333',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#00A896' : '#E0F7F5',
                          },
                        }),
                      }}
                    />
                  </div>

                  {/* Catégorie */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Catégorie</label>
                    <Select
                      value={filters.categorie && categories.length > 0
                        ? categories
                            .filter((c) => {
                              const categorieValue = c.id || c.nom;
                              return String(categorieValue) === String(filters.categorie) || c.nom === filters.categorie;
                            })
                            .map((c) => ({
                              value: c.id || c.nom,
                              label: c.nom || String(c.id || c.nom),
                            }))[0] || null
                        : null
                      }
                      onChange={(option) => handleFilterChange('categorie', option ? option.value : '')}
                      options={categories.map((c) => ({
                        value: c.id || c.nom,
                        label: c.nom || String(c.id || c.nom),
                      }))}
                      isClearable
                      placeholder="Toutes les catégories"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: '#d1d5db',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#00A896',
                          },
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected
                            ? '#00A896'
                            : state.isFocused
                            ? '#E0F7F5'
                            : 'white',
                          color: state.isSelected ? 'white' : '#333',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#00A896' : '#E0F7F5',
                          },
                        }),
                      }}
                    />
                  </div>

                  {/* Zone d'affectation */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Zone d'Affectation</label>
                    <input
                      type="text"
                      value={filters.zone_affectation}
                      onChange={(e) => handleFilterChange('zone_affectation', e.target.value)}
                      placeholder="Rechercher une zone..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>

                  {/* Lieu d'affectation */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Lieu d'Affectation</label>
                    <input
                      type="text"
                      value={filters.lieu_affectation}
                      onChange={(e) => handleFilterChange('lieu_affectation', e.target.value)}
                      placeholder="Rechercher un lieu..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>

                  {/* Date de naissance */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Date de naissance (de)</label>
                    <input
                      type="date"
                      value={filters.date_naissance_from}
                      onChange={(e) => handleFilterChange('date_naissance_from', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Date de naissance (à)</label>
                    <input
                      type="date"
                      value={filters.date_naissance_to}
                      onChange={(e) => handleFilterChange('date_naissance_to', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>

                  {/* Date d'affectation */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Date d'affectation (de)</label>
                    <input
                      type="date"
                      value={filters.date_affectation_from}
                      onChange={(e) => handleFilterChange('date_affectation_from', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Date d'affectation (à)</label>
                    <input
                      type="date"
                      value={filters.date_affectation_to}
                      onChange={(e) => handleFilterChange('date_affectation_to', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>

                  {/* Date d'enregistrement */}
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Date d'enregistrement (de)</label>
                    <input
                      type="date"
                      value={filters.date_enregistrement_from}
                      onChange={(e) => handleFilterChange('date_enregistrement_from', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">Date d'enregistrement (à)</label>
                    <input
                      type="date"
                      value={filters.date_enregistrement_to}
                      onChange={(e) => handleFilterChange('date_enregistrement_to', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Message */}
        {message.text && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-100 text-green-700 border border-green-400'
                : 'bg-red-100 text-red-700 border border-red-400'
            }`}
          >
            <i
              className={`fas ${
                message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'
              } mr-2`}
            ></i>
            {message.text}
          </div>
        )}

        {/* Results Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-12 bg-cardBg rounded-lg shadow-lg">
            <i className="fas fa-spinner fa-spin text-4xl text-primary"></i>
          </div>
        ) : !Array.isArray(agents) || agents.length === 0 ? (
          <div className="bg-cardBg rounded-lg shadow-lg p-12 text-center">
            <i className="fas fa-user-slash text-6xl text-gray-300 mb-4"></i>
            <p className="text-xl text-text-light">Aucun agent trouvé</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentAgents.map((agent, index) => {
              // Couleurs alternées pour les cards
              const colors = [
                { bg: 'bg-gradient-to-br from-blue-500 to-blue-600', icon: 'bg-blue-400' },
                { bg: 'bg-gradient-to-br from-purple-500 to-purple-600', icon: 'bg-purple-400' },
                { bg: 'bg-gradient-to-br from-pink-500 to-pink-600', icon: 'bg-pink-400' },
                { bg: 'bg-gradient-to-br from-indigo-500 to-indigo-600', icon: 'bg-indigo-400' },
                { bg: 'bg-gradient-to-br from-teal-500 to-teal-600', icon: 'bg-teal-400' },
                { bg: 'bg-gradient-to-br from-orange-500 to-orange-600', icon: 'bg-orange-400' },
              ];
              const colorScheme = colors[index % colors.length];
              const photoUrl = agent.photo && typeof agent.photo === 'string' && agent.photo.length > 0 ? agent.photo : null;
              
              return (
                <div
                  key={agent.id}
                  className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group"
                >
                  {/* Header avec gradient coloré */}
                  <div className={`${colorScheme.bg} p-6 text-white relative overflow-hidden`}>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full -ml-12 -mb-12"></div>
                    
                    <div className="relative z-10 flex items-center gap-4">
                      {/* Photo ou avatar */}
                      {photoUrl ? (
                        <div className="relative">
                          <img
                            src={photoUrl}
                            alt={`${agent.nom} ${agent.prenom}`}
                            className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.nextElementSibling;
                              if (fallback) fallback.classList.remove('hidden');
                            }}
                          />
                          <div className={`w-16 h-16 rounded-full ${colorScheme.icon} flex items-center justify-center text-white text-2xl font-bold border-4 border-white shadow-lg hidden`}>
                            {(agent.nom?.[0] || agent.prenom?.[0] || 'A').toUpperCase()}
                          </div>
                        </div>
                      ) : (
                        <div className={`w-16 h-16 rounded-full ${colorScheme.icon} flex items-center justify-center text-white text-2xl font-bold border-4 border-white shadow-lg`}>
                          {(agent.nom?.[0] || agent.prenom?.[0] || 'A').toUpperCase()}
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold truncate">{agent.nom} {agent.prenom}</h3>
                        <p className="text-blue-100 text-sm font-medium">{agent.matricule}</p>
                        {agent.documents_count > 0 && (
                          <div className="mt-2 inline-flex items-center gap-1 bg-white bg-opacity-20 px-2 py-1 rounded-full text-xs">
                            <i className="fas fa-images"></i>
                            <span>{agent.documents_count} document{agent.documents_count > 1 ? 's' : ''}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-6">
                    {/* Informations principales */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <i className="fas fa-building text-gray-400 w-5"></i>
                        <span className="text-gray-600">{agent.bureau_nom || agent.bureau || 'Non assigné'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <i className="fas fa-sitemap text-gray-400 w-5"></i>
                        <span className="text-gray-600">{agent.cellule_nom || agent.cellule || 'Non assigné'}</span>
                      </div>
                      {agent.grade && (
                        <div className="flex items-center gap-2 text-sm">
                          <i className="fas fa-star text-gray-400 w-5"></i>
                          <span className="text-gray-600">{getGradeName(agent.grade)}</span>
                        </div>
                      )}
                      {agent.fonction && (
                        <div className="flex items-center gap-2 text-sm">
                          <i className="fas fa-briefcase text-gray-400 w-5"></i>
                          <span className="text-gray-600">{getFonctionName(agent.fonction)}</span>
                        </div>
                      )}
                    </div>

                    {/* Aperçu des documents si disponibles */}
                    {agent.documents && agent.documents.length > 0 && (
                      <div className="mb-4 pb-4 border-b border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            <i className="fas fa-images"></i>
                            Documents ({agent.documents.length})
                          </span>
                        </div>
                        <div className="flex gap-2">
                          {agent.documents.slice(0, 3).map((doc, idx) => (
                            <div key={doc.id || idx} className="relative flex-1 aspect-square rounded-lg overflow-hidden border-2 border-gray-200">
                              <img
                                src={doc.cloudinary_url}
                                alt={doc.file_name || `Doc ${idx + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="12" dy="6" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EImg%3C/text%3E%3C/svg%3E';
                                }}
                              />
                            </div>
                          ))}
                          {agent.documents.length > 3 && (
                            <div className="flex-1 aspect-square rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-gray-300 flex items-center justify-center">
                              <span className="text-xs font-bold text-gray-600">+{agent.documents.length - 3}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Statut */}
                    <div className="mb-4">
                      <select
                        value={agent.statut || 'actif'}
                        onChange={(e) => handleStatutChange(agent.id, e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          agent.statut === 'actif'
                            ? 'bg-green-100 text-green-800 border-2 border-green-300 hover:bg-green-200'
                            : 'bg-red-100 text-red-800 border-2 border-red-300 hover:bg-red-200'
                        } outline-none cursor-pointer`}
                      >
                        <option value="actif">✓ Actif</option>
                        <option value="inactif">✗ Inactif</option>
                      </select>
                    </div>

                    {/* Actions */}
                    <div className="pt-4 border-t border-gray-200 overflow-x-auto">
                      <div className="flex gap-2 min-w-max">
                        <button
                          onClick={() => handleViewDetails(agent.id)}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center justify-center"
                          title="Voir les détails"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button
                          onClick={() => handleEditAgent(agent.id)}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all flex items-center justify-center"
                          title="Modifier l'agent"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          onClick={() => viewCarte(agent.matricule)}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all flex items-center justify-center"
                          title="Voir la carte"
                        >
                          <i className="fas fa-id-card"></i>
                        </button>
                        {userRole !== 'user' && (
                          <button
                            onClick={() => handleDeleteAgent(agent.id, `${agent.nom} ${agent.prenom}`)}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all flex items-center justify-center"
                            title="Supprimer l'agent"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {Array.isArray(agents) && agents.length > 0 && totalPages > 1 && (
          <div className="mt-6 bg-cardBg rounded-lg shadow-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-text-light">
                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(endIndex, agents.length)}</strong> sur <strong>{agents.length}</strong> agent(s)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                
                {/* Numéros de page */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // Afficher seulement certaines pages pour éviter trop de boutons
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-4 py-2 rounded-lg transition-all ${
                            currentPage === page
                              ? 'bg-primary text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span key={page} className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        {Array.isArray(agents) && agents.length > 0 && (
          <div className="mt-4 bg-cardBg rounded-lg shadow-lg p-4">
            <p className="text-text-light text-sm">
              <i className="fas fa-info-circle mr-2 text-primary"></i>
              Total: <strong>{agents.length}</strong> agent(s)
              {totalPages > 1 && (
                <span className="ml-4">
                  | Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong>
                </span>
              )}
            </p>
          </div>
        )}

        {/* Modale de détails */}
        {showDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-cardBg rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-cardBg border-b border-gray-200 p-6 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-text flex items-center">
                  <i className="fas fa-user-circle mr-3 text-primary"></i>
                  Détails de l'agent
                </h2>
                <button
                  onClick={closeDetails}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="p-6">
                {loadingDetails ? (
                  <div className="flex items-center justify-center py-12">
                    <i className="fas fa-spinner fa-spin text-4xl text-primary"></i>
                  </div>
                ) : selectedAgent ? (
                  <div className="space-y-6">
                    {/* Photo et nom */}
                    <div className="flex items-center mb-6 pb-6 border-b border-gray-200">
                      {selectedAgent.photo && typeof selectedAgent.photo === 'string' && selectedAgent.photo.length > 0 ? (
                        <div className="relative mr-6">
                          <img
                            src={selectedAgent.photo}
                            alt={`${selectedAgent.nom} ${selectedAgent.prenom}`}
                            className="w-24 h-24 rounded-full object-cover border-4 border-primary"
                            onError={(e) => {
                              console.error('Erreur chargement image:', selectedAgent.photo);
                              e.target.style.display = 'none';
                              const fallback = e.target.parentElement.querySelector('.photo-fallback');
                              if (fallback) fallback.classList.remove('hidden');
                            }}
                            onLoad={() => {
                              const fallback = document.querySelector('.photo-fallback');
                              if (fallback) fallback.classList.add('hidden');
                            }}
                          />
                          <div 
                            className="w-24 h-24 rounded-full bg-primary flex items-center justify-center text-white text-3xl font-bold absolute top-0 left-0 hidden photo-fallback"
                          >
                            {(selectedAgent.nom?.[0] || selectedAgent.prenom?.[0] || 'A').toUpperCase()}
                          </div>
                        </div>
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center text-white text-3xl font-bold mr-6">
                          {(selectedAgent.nom?.[0] || selectedAgent.prenom?.[0] || 'A').toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h3 className="text-2xl font-bold text-text">
                          {selectedAgent.nom} {selectedAgent.prenom}
                        </h3>
                        <p className="text-text-light">{selectedAgent.matricule}</p>
                      </div>
                    </div>

                    {/* Informations détaillées */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          ID Agent
                        </label>
                        <p className="text-text font-medium">{selectedAgent.id_agent || selectedAgent.id || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Matricule
                        </label>
                        <p className="text-text font-medium">{selectedAgent.matricule}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Nom
                        </label>
                        <p className="text-text font-medium">{selectedAgent.nom || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Prénom
                        </label>
                        <p className="text-text font-medium">{selectedAgent.prenom || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Sexe
                        </label>
                        <p className="text-text font-medium">{selectedAgent.sexe || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Date de naissance
                        </label>
                        <p className="text-text font-medium">
                          {selectedAgent.date_naissance
                            ? new Date(selectedAgent.date_naissance).toLocaleDateString('fr-FR')
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Lieu de naissance
                        </label>
                        <p className="text-text font-medium">{selectedAgent.lieu_naissance || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Nationalité
                        </label>
                        <p className="text-text font-medium">{selectedAgent.nationalite || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Téléphone
                        </label>
                        <p className="text-text font-medium">{selectedAgent.telephone || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Email
                        </label>
                        <p className="text-text font-medium">{selectedAgent.email || '-'}</p>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Adresse
                        </label>
                        <p className="text-text font-medium">{selectedAgent.adresse || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Catégorie
                        </label>
                        <p className="text-text font-medium">{selectedAgent.categorie || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Bureau
                        </label>
                        <p className="text-text font-medium">{selectedAgent.bureau_nom || selectedAgent.bureau || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Cellule
                        </label>
                        <p className="text-text font-medium">{selectedAgent.cellule_nom || selectedAgent.cellule || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Grade
                        </label>
                        <p className="text-text font-medium">{selectedAgent.grade || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Fonction
                        </label>
                        <p className="text-text font-medium">{selectedAgent.fonction || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-light mb-1">
                          Statut
                        </label>
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            selectedAgent.statut === 'actif'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {selectedAgent.statut === 'actif' ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                      {selectedAgent.date_affectation && (
                        <div>
                          <label className="block text-sm font-medium text-text-light mb-1">
                            Date d'Affectation
                          </label>
                          <p className="text-text font-medium">
                            {new Date(selectedAgent.date_affectation).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      )}
                      {selectedAgent.ref_affectation && (
                        <div>
                          <label className="block text-sm font-medium text-text-light mb-1">
                            Référence Affectation
                          </label>
                          <p className="text-text font-medium">{selectedAgent.ref_affectation}</p>
                        </div>
                      )}
                      {selectedAgent.zone_affectation && (
                        <div>
                          <label className="block text-sm font-medium text-text-light mb-1">
                            Zone d'Affectation
                          </label>
                          <p className="text-text font-medium">{selectedAgent.zone_affectation}</p>
                        </div>
                      )}
                      {selectedAgent.lieu_affectation && (
                        <div>
                          <label className="block text-sm font-medium text-text-light mb-1">
                            Lieu d'Affectation
                          </label>
                          <p className="text-text font-medium">{selectedAgent.lieu_affectation}</p>
                        </div>
                      )}
                      {selectedAgent.date_enregistrement && (
                        <div>
                          <label className="block text-sm font-medium text-text-light mb-1">
                            Date d'enregistrement
                          </label>
                          <p className="text-text font-medium">
                            {new Date(selectedAgent.date_enregistrement).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Documents Cloudinary */}
                    {selectedAgent.documents && selectedAgent.documents.length > 0 && (
                      <div className="pt-6 border-t border-gray-200">
                        <h4 className="text-lg font-semibold text-text mb-4 flex items-center">
                          <i className="fas fa-images mr-2 text-primary"></i>
                          Documents ({selectedAgent.documents.length})
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {selectedAgent.documents.map((doc, idx) => (
                            <div
                              key={doc.id || idx}
                              className="group relative bg-gray-50 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-primary transition-all cursor-pointer"
                            >
                              <div className="aspect-square relative">
                                <img
                                  src={doc.cloudinary_url}
                                  alt={doc.file_name || `Document ${idx + 1}`}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                  onError={(e) => {
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="20" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EImage%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                  <a
                                    href={doc.cloudinary_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="opacity-0 group-hover:opacity-100 bg-white text-primary p-3 rounded-full hover:bg-primary hover:text-white transition-all"
                                    title="Voir en grand"
                                  >
                                    <i className="fas fa-expand"></i>
                                  </a>
                                </div>
                              </div>
                              <div className="p-2">
                                <p className="text-xs text-gray-600 truncate" title={doc.file_name}>
                                  {doc.file_name || `Document ${idx + 1}`}
                                </p>
                                {doc.file_size && (
                                  <p className="text-xs text-gray-400">
                                    {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Documents anciens (CNI, Carte électeur) */}
                    {(selectedAgent.document_cni || selectedAgent.document_carte_electeur) && (
                      <div className="pt-6 border-t border-gray-200">
                        <h4 className="text-lg font-semibold text-text mb-4 flex items-center">
                          <i className="fas fa-file-alt mr-2 text-primary"></i>
                          Documents d'identification
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedAgent.document_cni && (
                            <div>
                              <label className="block text-sm font-medium text-text-light mb-2">
                                Document CNI
                              </label>
                              {selectedAgent.document_cni.startsWith('data:') || selectedAgent.document_cni.startsWith('http') ? (
                                <a
                                  href={selectedAgent.document_cni}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                                >
                                  <i className="fas fa-file-pdf mr-2"></i>
                                  Voir le document
                                </a>
                              ) : (
                                <p className="text-text-light text-sm">Document non disponible</p>
                              )}
                            </div>
                          )}
                          {selectedAgent.document_carte_electeur && (
                            <div>
                              <label className="block text-sm font-medium text-text-light mb-2">
                                Carte d'électeur
                              </label>
                              {selectedAgent.document_carte_electeur.startsWith('data:') || selectedAgent.document_carte_electeur.startsWith('http') ? (
                                <a
                                  href={selectedAgent.document_carte_electeur}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                                >
                                  <i className="fas fa-file-pdf mr-2"></i>
                                  Voir le document
                                </a>
                              ) : (
                                <p className="text-text-light text-sm">Document non disponible</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-text-light">Impossible de charger les détails</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modale de modification */}
        {showEditForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-cardBg rounded-lg shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto my-8">
              <div className="sticky top-0 bg-cardBg border-b border-gray-200 p-6 flex justify-between items-center z-10">
                <h2 className="text-2xl font-bold text-text flex items-center">
                  <i className="fas fa-edit mr-3 text-primary"></i>
                  Modifier l'Agent
                </h2>
                <button
                  onClick={closeEditForm}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="p-6">
                {editLoading && !editFormData.nom ? (
                  <div className="flex items-center justify-center py-12">
                    <i className="fas fa-spinner fa-spin text-4xl text-primary"></i>
                  </div>
                ) : (
                  <form onSubmit={handleEditSubmit} className="space-y-8">
                    {/* Photo Section */}
                    <div>
                      <h3 className="text-xl font-semibold text-text mb-4">
                        <i className="fas fa-camera mr-2 text-primary"></i>Photo
                      </h3>
                      <div className="flex items-center gap-4">
                        {editPhotoPreview ? (
                          <div className="relative">
                            <img
                              src={editPhotoPreview}
                              alt="Preview"
                              className="w-32 h-32 object-cover rounded-lg border-2 border-primary"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setEditPhoto(null);
                                setEditPhotoPreview(null);
                              }}
                              className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        ) : (
                          <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                            <i className="fas fa-image text-3xl text-gray-400"></i>
                          </div>
                        )}
                        <div>
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                          >
                            <i className="fas fa-upload mr-2"></i>Changer la photo
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleEditFileChange}
                            className="hidden"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Informations Personnelles */}
                    <div>
                      <h3 className="text-xl font-semibold text-text mb-4">
                        <i className="fas fa-user mr-2 text-primary"></i>Informations Personnelles
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Nom *</label>
                          <input
                            type="text"
                            name="nom"
                            value={editFormData.nom || ''}
                            onChange={handleEditInputChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Prénom *</label>
                          <input
                            type="text"
                            name="prenom"
                            value={editFormData.prenom || ''}
                            onChange={handleEditInputChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Post-nom</label>
                          <input
                            type="text"
                            name="post_nom"
                            value={editFormData.post_nom || ''}
                            onChange={handleEditInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Sexe *</label>
                          <select
                            name="sexe"
                            value={editFormData.sexe || ''}
                            onChange={handleEditInputChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          >
                            <option value="">Sélectionner</option>
                            <option value="M">Masculin</option>
                            <option value="F">Féminin</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">État Civil</label>
                          <Select
                            value={etatCivilOptions.find(option => option.value === editFormData.etat_civil) || null}
                            onChange={(option) => setEditFormData(prev => ({ ...prev, etat_civil: option ? option.value : '' }))}
                            options={etatCivilOptions}
                            isClearable
                            placeholder="Sélectionner l'état civil"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderColor: '#d1d5db',
                                boxShadow: 'none',
                                '&:hover': { borderColor: '#00A896' },
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isSelected ? '#00A896' : state.isFocused ? '#E0F7F5' : 'white',
                                color: state.isSelected ? 'white' : '#333',
                              }),
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Titre Académique</label>
                          <Select
                            value={titreAcademiqueOptions.find(option => option.value === editFormData.titre_academique) || null}
                            onChange={(option) => setEditFormData(prev => ({ ...prev, titre_academique: option ? option.value : '' }))}
                            options={titreAcademiqueOptions}
                            isClearable
                            isSearchable
                            placeholder="Sélectionner le titre académique"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderColor: '#d1d5db',
                                boxShadow: 'none',
                                '&:hover': { borderColor: '#00A896' },
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isSelected ? '#00A896' : state.isFocused ? '#E0F7F5' : 'white',
                                color: state.isSelected ? 'white' : '#333',
                              }),
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Filière</label>
                          <input
                            type="text"
                            name="filiere"
                            value={editFormData.filiere || ''}
                            onChange={handleEditInputChange}
                            placeholder="Entrer la filière"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Date de naissance</label>
                          <input
                            type="date"
                            name="date_naissance"
                            value={editFormData.date_naissance || ''}
                            onChange={handleEditInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Lieu de naissance</label>
                          <input
                            type="text"
                            name="lieu_naissance"
                            value={editFormData.lieu_naissance || ''}
                            onChange={handleEditInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Nationalité</label>
                          <input
                            type="text"
                            name="nationalite"
                            value={editFormData.nationalite || ''}
                            onChange={handleEditInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Téléphone</label>
                          <input
                            type="tel"
                            name="telephone"
                            value={editFormData.telephone || ''}
                            onChange={handleEditInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Email</label>
                          <input
                            type="email"
                            name="email"
                            value={editFormData.email || ''}
                            onChange={handleEditInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-text mb-2">Adresse</label>
                          <textarea
                            name="adresse"
                            value={editFormData.adresse || ''}
                            onChange={handleEditInputChange}
                            rows="3"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Informations Professionnelles */}
                    <div>
                      <h3 className="text-xl font-semibold text-text mb-4">
                        <i className="fas fa-briefcase mr-2 text-primary"></i>Informations Professionnelles
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">ID Agent</label>
                          <input
                            type="text"
                            name="id_agent"
                            value={editFormData.id_agent || ''}
                            onChange={handleEditInputChange}
                            readOnly
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Matricule</label>
                          <input
                            type="text"
                            name="matricule"
                            value={editFormData.matricule || ''}
                            onChange={handleEditInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Catégorie *</label>
                          <Select
                            value={editFormData.categorie && categories.length > 0
                              ? categories.filter((c) => {
                                  const catValue = c.id || c.nom;
                                  return String(catValue) === String(editFormData.categorie) || c.nom === editFormData.categorie;
                                }).map((c) => ({
                                  value: c.id || c.nom,
                                  label: c.nom || String(c.id || c.nom),
                                }))[0] || null
                              : null
                            }
                            onChange={(option) => setEditFormData(prev => ({ ...prev, categorie: option ? option.value : '' }))}
                            options={categories.map((c) => ({
                              value: c.id || c.nom,
                              label: c.nom || String(c.id || c.nom),
                            }))}
                            isClearable
                            isSearchable
                            placeholder="Sélectionner une catégorie"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderColor: '#d1d5db',
                                boxShadow: 'none',
                                '&:hover': { borderColor: '#00A896' },
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isSelected ? '#00A896' : state.isFocused ? '#E0F7F5' : 'white',
                                color: state.isSelected ? 'white' : '#333',
                              }),
                            }}
                          />
                        </div>
                        
                        {/* Checkboxes pour "Sous statut" */}
                        {isSousStatut(editFormData.categorie) && (
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-text mb-3">Options Sous Statut</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  name="arrete"
                                  checked={editFormData.arrete || false}
                                  onChange={handleEditCheckboxChange}
                                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                />
                                <span className="ml-2 text-sm text-text">Arrêté</span>
                              </label>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  name="commission_affectation_sg"
                                  checked={editFormData.commission_affectation_sg || false}
                                  onChange={handleEditCheckboxChange}
                                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                />
                                <span className="ml-2 text-sm text-text">Commission d'affectation SG</span>
                              </label>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  name="commission_affectation_locale"
                                  checked={editFormData.commission_affectation_locale || false}
                                  onChange={handleEditCheckboxChange}
                                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                />
                                <span className="ml-2 text-sm text-text">Commission d'affectation Locale</span>
                              </label>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  name="notification_fonc_publique_ville"
                                  checked={editFormData.notification_fonc_publique_ville || false}
                                  onChange={handleEditCheckboxChange}
                                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                />
                                <span className="ml-2 text-sm text-text">Notification Fonc Publique/Ville</span>
                              </label>
                            </div>
                          </div>
                        )}

                        {/* Checkboxes pour "Nouvelle unité" */}
                        {isNouvelleUnite(editFormData.categorie) && (
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-text mb-3">Options Nouvelle Unité</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  name="commission_local_fonc_publique_ville"
                                  checked={editFormData.commission_local_fonc_publique_ville || false}
                                  onChange={handleEditCheckboxChange}
                                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                />
                                <span className="ml-2 text-sm text-text">Commission local fonction Publique/ville</span>
                              </label>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  name="notification_nu"
                                  checked={editFormData.notification_nu || false}
                                  onChange={handleEditCheckboxChange}
                                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                />
                                <span className="ml-2 text-sm text-text">Notification N.U</span>
                              </label>
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Bureau</label>
                          <Select
                            value={editFormData.bureau && bureaux.length > 0
                              ? bureaux.filter((b) => {
                                  const bureauValue = b.id || b.nom;
                                  return String(bureauValue) === String(editFormData.bureau) || b.nom === editFormData.bureau;
                                }).map((b) => ({
                                  value: b.id || b.nom,
                                  label: b.nom || String(b.id || b.nom),
                                }))[0] || null
                              : null
                            }
                            onChange={(option) => setEditFormData(prev => ({ ...prev, bureau: option ? option.value : '' }))}
                            options={bureaux.map((b) => ({
                              value: b.id || b.nom,
                              label: b.nom || String(b.id || b.nom),
                            }))}
                            isClearable
                            isSearchable
                            placeholder="Sélectionner un bureau"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderColor: '#d1d5db',
                                boxShadow: 'none',
                                '&:hover': { borderColor: '#00A896' },
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isSelected ? '#00A896' : state.isFocused ? '#E0F7F5' : 'white',
                                color: state.isSelected ? 'white' : '#333',
                              }),
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Cellule</label>
                          <Select
                            value={editFormData.cellule && cellules.length > 0
                              ? cellules.filter((c) => {
                                  const celluleValue = c.id || c.nom;
                                  return String(celluleValue) === String(editFormData.cellule) || c.nom === editFormData.cellule;
                                }).map((c) => ({
                                  value: c.id || c.nom,
                                  label: c.nom || String(c.id || c.nom),
                                }))[0] || null
                              : null
                            }
                            onChange={(option) => setEditFormData(prev => ({ ...prev, cellule: option ? option.value : '' }))}
                            options={cellules.map((c) => ({
                              value: c.id || c.nom,
                              label: c.nom || String(c.id || c.nom),
                            }))}
                            isClearable
                            isSearchable
                            placeholder="Sélectionner une cellule"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderColor: '#d1d5db',
                                boxShadow: 'none',
                                '&:hover': { borderColor: '#00A896' },
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isSelected ? '#00A896' : state.isFocused ? '#E0F7F5' : 'white',
                                color: state.isSelected ? 'white' : '#333',
                              }),
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Grade</label>
                          <Select
                            value={editFormData.grade && grades.length > 0
                              ? grades.filter((g) => {
                                  const gradeValue = g.id || g.nom;
                                  return String(gradeValue) === String(editFormData.grade) || g.nom === editFormData.grade;
                                }).map((g) => ({
                                  value: g.id || g.nom,
                                  label: g.nom || String(g.id || g.nom),
                                }))[0] || null
                              : null
                            }
                            onChange={(option) => setEditFormData(prev => ({ ...prev, grade: option ? option.value : '' }))}
                            options={grades.map((g) => ({
                              value: g.id || g.nom,
                              label: g.nom || String(g.id || g.nom),
                            }))}
                            isClearable
                            isSearchable
                            placeholder="Sélectionner un grade"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderColor: '#d1d5db',
                                boxShadow: 'none',
                                '&:hover': { borderColor: '#00A896' },
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isSelected ? '#00A896' : state.isFocused ? '#E0F7F5' : 'white',
                                color: state.isSelected ? 'white' : '#333',
                              }),
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Fonction</label>
                          <Select
                            value={editFormData.fonction && fonctions.length > 0
                              ? fonctions.filter((f) => {
                                  const fonctionValue = f.id || f.nom;
                                  return String(fonctionValue) === String(editFormData.fonction) || f.nom === editFormData.fonction;
                                }).map((f) => ({
                                  value: f.id || f.nom,
                                  label: f.nom || String(f.id || f.nom),
                                }))[0] || null
                              : null
                            }
                            onChange={(option) => setEditFormData(prev => ({ ...prev, fonction: option ? option.value : '' }))}
                            options={fonctions.map((f) => ({
                              value: f.id || f.nom,
                              label: f.nom || String(f.id || f.nom),
                            }))}
                            isClearable
                            isSearchable
                            placeholder="Sélectionner une fonction"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderColor: '#d1d5db',
                                boxShadow: 'none',
                                '&:hover': { borderColor: '#00A896' },
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isSelected ? '#00A896' : state.isFocused ? '#E0F7F5' : 'white',
                                color: state.isSelected ? 'white' : '#333',
                              }),
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Situation Prime</label>
                          <Select
                            value={situationPrimeOptions.find(option => option.value === editFormData.situation_prime) || null}
                            onChange={(option) => setEditFormData(prev => ({ ...prev, situation_prime: option ? option.value : '' }))}
                            options={situationPrimeOptions}
                            isClearable
                            placeholder="Sélectionner la situation"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderColor: '#d1d5db',
                                boxShadow: 'none',
                                '&:hover': { borderColor: '#00A896' },
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isSelected ? '#00A896' : state.isFocused ? '#E0F7F5' : 'white',
                                color: state.isSelected ? 'white' : '#333',
                              }),
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Date d'Affectation</label>
                          <input
                            type="date"
                            name="date_affectation"
                            value={editFormData.date_affectation || ''}
                            onChange={handleEditInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Référence Affectation</label>
                          <input
                            type="text"
                            name="ref_affectation"
                            value={editFormData.ref_affectation || ''}
                            onChange={handleEditInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Zone d'Affectation</label>
                          <input
                            type="text"
                            name="zone_affectation"
                            value={editFormData.zone_affectation || ''}
                            onChange={handleEditInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text mb-2">Lieu d'Affectation</label>
                          <input
                            type="text"
                            name="lieu_affectation"
                            value={editFormData.lieu_affectation || ''}
                            onChange={handleEditInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Boutons d'action */}
                    <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={closeEditForm}
                        className="px-6 py-3 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-all"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        disabled={editLoading}
                        className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {editLoading ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>Enregistrement...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save mr-2"></i>Enregistrer les modifications
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Recherche;

